import { Box, IconButton, Skeleton, Stack, Tooltip } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { structurePipesByStructureId } from '@/components/Analysis/state/atoms';
import {
  EquipmentDetail,
  DetailLabel,
  DetailValue,
  StyledDivider,
  AssemblyNavigationStyle,
} from './styles';
import { formatEnumForDisplay, getPipeSize } from '@/utils';
import {
  GetAssemblyDetailsQuery,
  useScCategoryMappingQuery,
  useGetStructureDetailsQuery,
  useGetStructurePipesQuery,
  usePointOfInterestsCountLazyQuery,
  UnitsEnum,
} from '@/__generated__/graphql';
import { EquipmentDetailRenderer } from './EquipmentDetailRenderer';
import { useFeatureFlag, useHavePermission, useRouter } from '@/hooks';
import { Permissions } from '@/types';
import * as state from '@/components/Analysis/state';
import { localiseMeasurement } from '@/utils/unitSystem';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import {
  ALARM_LEVEL_MANUALLY_SET_TEMPLATE_KEY,
  ALARM_LEVEL_TEMPLATE_KEY,
  AlarmLevelSettingStatus,
  LocationTag,
  NOT_APPLICABLE_LABEL,
  PIPES_AND_SPAN_KEYS,
} from '@/constants';
import { CriticalityLevel } from './CriticalityLevel';
import { PipeSpec } from './PipeSpec';
import { Viewpoints } from './AssociatedViewpoints/Viewpoints';
import { Blisters } from './AssociatedBlisters/Blisters';
import { ScCategory } from './ScCategory';
import { MediaUploads } from './MediaUploads/MediaUploads';
import { Defects } from './AssociatedDefects/Defects';
import { Detail } from './StyledComponents';
import { Priority } from './Priority';
import { AssemblyNavigation } from '@/components/Analysis/ReviewScreen/AssemblyNavigation';
import { useGetAssemblies } from '@/components/Analysis/ReviewScreen/hooks/useGetAssemblies';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { ComponentSettings } from '@/components/Analysis/Insights/ComponentSettings/ComponentSettings';
import { PipesAndSpanComponents } from '../PipesAndSpanTables/PipesAndSpanComponents';
import { useUnitConversion } from '@/hooks/useUnitConversion';
import { AllowedNomination } from './AllowedNomination/AllowedNomination';

type Props = {
  assembly?: GetAssemblyDetailsQuery['assembly'];
  isLoadingAssemblyDetails?: boolean;
  isUncontextualized?: boolean;
};

const existingKeys = new Set([
  'risk_level_1_component',
  'risk_level_2_component',
  'risk_component_type',
  'risk_material_type',
  'risk_member_id',
  'risk_structure',
  'risk_equipment_tag',
]);

export const EquipmentDetails = ({
  assembly,
  isLoadingAssemblyDetails,
  isUncontextualized,
}: Props) => {
  const assemblyId = useAtomValue(state.selectedAssemblyId);
  const isEsSheet = useAtomValue(state.hasRiskAssessment);
  const structureId = useGetStructureId();
  const [structurePipes, setStructurePipes] = useAtom(structurePipesByStructureId);
  const canReadDefects = useHavePermission(Permissions.READ_DEFECTS);
  const canAccessAitTools = useFeatureFlag(['ait-engineer', 'admin', 'ait-task-lead']);
  const isWorkInProgress = useFeatureFlag('work-in-progress');
  const unitSystem = useAtomValue(state.unitSystem);
  const assemblyTemplate = useAtomValue(state.assemblyTemplate);
  const hasConditionGradeField = useMemo(() => assemblyTemplate?.fields?.some((f) => f.key === 'condition_grade'), [assemblyTemplate]);
  const [openComponentSettings, setOpenComponentSettings] = useState<boolean>(false);
  const router = useRouter();

  useGetAssemblies();

  const isAtESSheet = useMemo(() => router.pathname.includes('risk'), [router.pathname]);
  const { getUnitSuffix } = useUnitConversion();
  const { data, loading: scCategoryLoading } = useScCategoryMappingQuery({
    variables: { structureId },
    skip: !structureId,
  });

  const { data: structureDetailsData } = useGetStructureDetailsQuery({
    variables: { structureId },
    skip: !structureId,
    fetchPolicy: 'cache-first',
  });

  const { data: structurePipesData, loading: pipesLoading } = useGetStructurePipesQuery({
    variables: { structureId },
    skip: !structureId,
    fetchPolicy: 'cache-first',
  });
  useEffect(() => {
    const structurePipes = structurePipesData?.structure?.structurePipesConfig?.imperialSteelPipes;
    if (structureId && structurePipes) {
      setStructurePipes((prev) => ({
        ...prev,
        [structureId]: structurePipes as Record<string, number>,
      }));
    }
  }, [structureId, structurePipesData, setStructurePipes]);

  const structureSteelPipes = useMemo(
    () => structurePipes[structureId],
    [structurePipes, structureId]
  );

  // Checking if the Alarm Level was manually set or auto-generated
  const alarmLevelSettingSubtitleText = useMemo(() => {
    // If theres alarm level is not set, dont show any subtitle
    if (!assembly?.templateFieldData?.[ALARM_LEVEL_TEMPLATE_KEY]) {
      return undefined;
    }

    return assembly?.templateFieldData?.[ALARM_LEVEL_MANUALLY_SET_TEMPLATE_KEY]
      ? AlarmLevelSettingStatus.MANUALLY_ADDED
      : AlarmLevelSettingStatus.AUTO_GENERATED;
  }, [assembly?.templateFieldData]);

  const scCategoryMappingExists = useMemo(() => {
    return Object.keys(data?.structure?.scCategoryMapping ?? {}).length > 0;
  }, [data?.structure?.scCategoryMapping]);

  const [totalPoiCount, setTotalPoiCount] = useState(0);

  const damageClassFieldExists = useMemo(() => {
    return !!assembly?.templateFieldData?.risk_damage_class;
  }, [assembly?.templateFieldData?.risk_damage_class]);

  const [getPOICount, { loading: poiCountLoading }] = usePointOfInterestsCountLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const loading = useMemo(
    () => isLoadingAssemblyDetails || poiCountLoading || scCategoryLoading || pipesLoading,
    [isLoadingAssemblyDetails, poiCountLoading, scCategoryLoading, pipesLoading]
  );

  useEffect(() => {
    if (assemblyId && !isUncontextualized) {
      const fetchPoiCounts = async () => {
        const { data: totalPoiCountData } = await getPOICount({
          variables: {
            input: {
              structureId,
              assemblyIds: [assemblyId],
              includeManuallyCreated: true,
            },
          },
        });

        setTotalPoiCount(totalPoiCountData?.pointOfInterestsCount?.count ?? 0);
      };

      fetchPoiCounts();
    }
  }, [assemblyId, getPOICount, isUncontextualized, structureId]);

  const unContextualizedGeneralDetails = useMemo(() => {
    const fieldData = assembly?.templateFieldData || {};
    const fields = [
      { label: 'Level 1 Component', key: 'risk_level_1_component' },
      { label: 'Level 2 Component', key: 'risk_level_2_component' },
      { label: 'Component type', key: 'risk_component_type' },
      { label: 'Structure', key: 'risk_structure' },
      { label: 'Member Id', key: 'risk_member_id' },
      { label: 'Material type', key: 'risk_material_type' },
    ];

    return fields.map(({ label, key }) => ({
      label,
      value: fieldData[key] ?? NOT_APPLICABLE_LABEL,
    }));
  }, [assembly?.templateFieldData]);

  const componentTemplateData = useMemo(() => {
    const area = `${localiseMeasurement({
      unitSystem,
      sourceUnit: UnitsEnum.SquareMeter,
      value: assembly?.templateFieldData?.area ?? assembly?.templateFieldData?.totalArea ?? 0,
    })}`;
    const { criticality, size, service } = assembly?.templateFieldData || {};

    const pipeSize = getPipeSize({
      size,
      unitSystem,
      structureSteelPipes,
      getUnitSuffix,
    });

    return [
      { title: 'Criticality', value: criticality },
      { title: 'Area', value: area },
      { title: 'Size', value: pipeSize },
      { title: 'Service Class', value: service },
      { title: 'POIs of Component', value: totalPoiCount },
    ].filter(({ value }) => value !== undefined && value !== null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    assembly?.area,
    assembly?.templateFieldData,
    getUnitSuffix,
    structureSteelPipes,
    totalPoiCount,
    unitSystem,
  ]);

  const hasCUIPoi = useMemo(() => {
    return assemblyTemplate?.fields?.find(({ key }) => key === 'cui_priority');
  }, [assemblyTemplate]);

  const isBpStructure = useMemo(() => {
    return assemblyTemplate?.fields?.find(({ key }) => key === 'governing_sc_category_bp');
  }, [assemblyTemplate]);

  const hasRecommendationField = useMemo(() => {
    if (!assemblyTemplate) return undefined;
    return assemblyTemplate.recommendation;
  }, [assemblyTemplate]);

  if (loading) {
    return (
      <Box sx={{ p: 3, pt: 1 }}>
        <Stack spacing={2} sx={{ py: 1 }} data-testid="pois-loader">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} variant="rectangular" height={26} />
          ))}
        </Stack>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: 3, pt: 1 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: '100%' }}
        >
          <AssemblyNavigation
            showTooltipTitle={true}
            sx={AssemblyNavigationStyle}
            location={LocationTag.ViewerComponents}
          />
          {isWorkInProgress && (
            <IconButton
              onClick={() => setOpenComponentSettings(true)}
              data-testid="insights-component-settings"
              sx={{ cursor: 'pointer' }}
            >
              <AbyssIcon iconName="gear" size="lg" />
            </IconButton>
          )}
        </Stack>

        <Stack direction="column" spacing={1} sx={{ mt: 1 }}>
          {canReadDefects && scCategoryMappingExists && <ScCategory />}
          {canReadDefects && hasCUIPoi && <Priority />}
        </Stack>

        {hasRecommendationField && isAtESSheet && <CriticalityLevel isolated />}

        <StyledDivider />

        {hasRecommendationField && !isAtESSheet && (
          <>
            {damageClassFieldExists && (
              <Detail title="Damage Class" insideAccordion={false}>
                <CriticalityLevel />
              </Detail>
            )}

            {!damageClassFieldExists && !isBpStructure && (
              <Detail
                title="Alarm Levels"
                insideAccordion={false}
                subtitle={alarmLevelSettingSubtitleText}
              >
                <CriticalityLevel />
              </Detail>
            )}
          </>
        )}

        {!isUncontextualized && (
          <>
            {componentTemplateData.map(({ title, value }, index) => (
              <Detail
                key={`${title}-${index}`}
                title={title}
                value={value}
                insideAccordion={false}
              />
            ))}

            {assembly?.templateFieldData && canReadDefects && (
              <EquipmentDetailRenderer
                data={assembly?.templateFieldData}
                area={assembly?.area ?? 0}
              />
            )}

            {canAccessAitTools && (
              <>
                <PipeSpec />
                {!hasConditionGradeField && (
                  <Blisters assemblyId={assemblyId} />
                )}
                <AllowedNomination
                  assemblyId={assemblyId}
                  poiTemplateNominationTypes={structureDetailsData?.structure?.nominationConfig?.poiTemplateNominationTypes}
                  nominationTableConfig={structureDetailsData?.structure?.nominationConfig?.nominationTableConfig}
                  hasConditionGradeField={hasConditionGradeField}
                />
                <Defects assemblyId={assemblyId} />
              </>
            )}
            <Viewpoints assemblyId={assemblyId} />
            {PIPES_AND_SPAN_KEYS.map((fieldKey) => {
              return (
                <PipesAndSpanComponents
                  key={fieldKey}
                  assemblyId={assemblyId}
                  fieldKey={fieldKey}
                ></PipesAndSpanComponents>
              );
            })}

            {assemblyId && (
              <MediaUploads
                referenceId={assemblyId}
                collectionName={'assembly'}
                files={assembly?.files || []}
                assembly={assembly}
                location={LocationTag.ViewerSelectedComponent}
              />
            )}
          </>
        )}

        {isUncontextualized && (
          <Box>
            {isEsSheet && (
              <Stack my="3.2rem" gap="1.6rem">
                {unContextualizedGeneralDetails.map((detail, index) => (
                  <EquipmentDetail key={detail.label + index}>
                    <Tooltip title={detail.label}>
                      <DetailLabel>{detail.label}</DetailLabel>
                    </Tooltip>
                    <Tooltip title={detail.value}>
                      <DetailValue>{detail.value}</DetailValue>
                    </Tooltip>
                  </EquipmentDetail>
                ))}
              </Stack>
            )}
            <StyledDivider />
            <Stack mt="3.2rem" gap="1.6rem">
              {Object.keys(assembly?.templateFieldData)
                .filter((key) => !existingKeys.has(key))
                .map((key, index) => (
                  <EquipmentDetail key={assembly?.templateFieldData[key] + index}>
                    <Tooltip title={formatEnumForDisplay(key).replace('Risk', '')}>
                      <DetailLabel>{formatEnumForDisplay(key).replace('Risk', '')}</DetailLabel>
                    </Tooltip>
                    <Tooltip title={assembly?.templateFieldData[key] || NOT_APPLICABLE_LABEL}>
                      <DetailValue>
                        {assembly?.templateFieldData[key] || NOT_APPLICABLE_LABEL}
                      </DetailValue>
                    </Tooltip>
                  </EquipmentDetail>
                ))}
            </Stack>
          </Box>
        )}
      </Box>
      {openComponentSettings && (
        <ComponentSettings
          selectedAssemblyIds={[assembly?.id ?? '']}
          isOpen={openComponentSettings}
          setIsOpen={setOpenComponentSettings}
        />
      )}
    </>
  );
};
